import { Box, Stack, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import React, { useState } from 'react';

import { colors } from '../../theme/theme';
import { formatCurrency, formatDate, parseScheduledPayments } from '../MessageBubbleHelpers';

type SortField = 'contractId' | 'scheduledDate' | 'amount' | 'status';
type SortDirection = 'asc' | 'desc';

export const ScheduledPaymentsCard: React.FC<{
    scheduledPayments: ReturnType<typeof parseScheduledPayments>;
    botText: string;
    brandPrimaryColor: string;
}> = ({ scheduledPayments, botText, brandPrimaryColor }) => {
    const [sortField, setSortField] = useState<SortField>('scheduledDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    if (!scheduledPayments) return null;
    const scheduleGuidanceFallback = (() => {
        const _gIdx = botText.search(/For complete Scheduled Payment/i);
        return _gIdx >= 0 ? botText.slice(_gIdx).trim() : null;
    })();
    const followUpText =
        scheduledPayments.followUp && /navigate to/i.test(scheduledPayments.followUp)
            ? scheduledPayments.followUp
            : scheduleGuidanceFallback || scheduledPayments.followUp;

    const formatDisplayDate = (value: string) => {
        if (!value || value === '-') {
            return '-';
        }
        return formatDate(value);
    };

    const getStatusStyles = (status: string) => {
        const normalized = (status || '').toLowerCase();
        if (normalized.includes('cancel') || normalized.includes('declined') || normalized.includes('failed')) {
            return { bg: '#FFE4E8', color: '#BE123C' };
        }
        if (normalized.includes('pending')) {
            return { bg: '#FFF3D6', color: '#9B6B00' };
        }
        if (normalized.includes('complete') || normalized.includes('processed') || normalized.includes('success')) {
            return { bg: '#DDF3E6', color: '#1F7A4D' };
        }
        return { bg: '#E7F0FF', color: '#1D4ED8' };
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortedPayments = () => {
        const sorted = [...scheduledPayments.payments].sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;

            if (sortField === 'contractId') {
                aVal = a.contractId || '';
                bVal = b.contractId || '';
            } else if (sortField === 'scheduledDate') {
                aVal = a.scheduledDate || '';
                bVal = b.scheduledDate || '';
            } else if (sortField === 'amount') {
                aVal = parseFloat(a.amount) || 0;
                bVal = parseFloat(b.amount) || 0;
            } else if (sortField === 'status') {
                aVal = a.status || '';
                bVal = b.status || '';
            } else {
                return 0;
            }

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = (bVal as string).toLowerCase();
                return sortDirection === 'asc'
                    ? aVal.localeCompare(bVal as string)
                    : (bVal as string).localeCompare(aVal);
            } else {
                return sortDirection === 'asc'
                    ? (aVal as number) - (bVal as number)
                    : (bVal as number) - (aVal as number);
            }
        });

        return sorted;
    };

    const getSortIndicator = (field: SortField) => {
        if (sortField !== field) return '';
        return sortDirection === 'asc' ? ' ▲' : ' ▼';
    };

    const sortedPayments = getSortedPayments();

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: colors.greyPreset[50],
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                    }}
                >
                    Scheduled Payments
                </Typography>
            </Stack>

            {scheduledPayments.intro && (
                <Typography
                    variant="body2"
                    sx={{
                        mb: 1.2,
                        color: colors.fullBlack,
                        lineHeight: 1.5,
                        fontSize: '0.95rem',
                    }}
                >
                    {scheduledPayments.intro}
                </Typography>
            )}

            <Box sx={{ overflowX: 'auto', mb: 1.5 }}>
                <Table sx={{ minWidth: 500 }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: colors.greyPreset[5] }}>
                            <TableCell
                                onClick={() => handleSort('contractId')}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    userSelect: 'none',
                                    '&:hover': { backgroundColor: colors.greyPreset[10] },
                                }}
                            >
                                Contract ID{getSortIndicator('contractId')}
                            </TableCell>
                            <TableCell
                                onClick={() => handleSort('scheduledDate')}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    userSelect: 'none',
                                    '&:hover': { backgroundColor: colors.greyPreset[10] },
                                }}
                            >
                                Scheduled Date{getSortIndicator('scheduledDate')}
                            </TableCell>
                            <TableCell
                                align="right"
                                onClick={() => handleSort('amount')}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    userSelect: 'none',
                                    '&:hover': { backgroundColor: colors.greyPreset[10] },
                                }}
                            >
                                Amount Due{getSortIndicator('amount')}
                            </TableCell>
                            <TableCell
                                onClick={() => handleSort('status')}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    textTransform: 'uppercase',
                                    userSelect: 'none',
                                    '&:hover': { backgroundColor: colors.greyPreset[10] },
                                }}
                            >
                                Status{getSortIndicator('status')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedPayments.map((payment, idx) => {
                            const statusStyles = getStatusStyles(payment.status);
                            return (
                                <TableRow key={`${payment.contractId}-${payment.scheduledDate}-${idx}`}>
                                    <TableCell sx={{ fontSize: '0.9rem' }}>{payment.contractId || '-'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.9rem' }}>
                                        {formatDisplayDate(payment.scheduledDate)}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{ fontSize: '0.9rem', fontWeight: 600, color: brandPrimaryColor }}
                                    >
                                        {payment.amount && payment.amount !== '-'
                                            ? formatCurrency(payment.amount)
                                            : '-'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.9rem' }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                px: 0.9,
                                                py: 0.35,
                                                borderRadius: '6px',
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                backgroundColor: statusStyles.bg,
                                                color: statusStyles.color,
                                                whiteSpace: 'nowrap',
                                                textTransform: 'capitalize',
                                                display: 'inline-block',
                                            }}
                                        >
                                            {payment.status}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>

            {followUpText && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 1.5,
                        color: colors.greyPreset[60],
                        whiteSpace: 'pre-line',
                        lineHeight: 1.6,
                        fontSize: '0.9rem',
                    }}
                >
                    {followUpText}
                </Typography>
            )}
        </Box>
    );
};
